import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import PageHeader from "../components/common/PageHeader";
import FormField from "../components/common/FormField";
import ReceiptScanner from "../components/ReceiptScanner";
import { useControlCash, CURRENCY_OPTIONS } from "../store/controlCashStore";
import { useToast } from "../contexts/ToastContext";
import { getTodayDate } from "../utils/common";
import { LEGACY_CATEGORY_OTHER } from "../constants";

/**
 * Flujo rápido: sube foto → escaneo automático → categoría → guardar.
 * Mínimo de campos para agregar un gasto desde recibo.
 */
const QuickAddExpense = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const monthId = searchParams.get("monthId");
  const { state, dispatch, formatDisplayAmount } = useControlCash();
  const toast = useToast();

  const [amount, setAmount] = useState(null);
  const [currency, setCurrency] = useState("PEN");
  const [category, setCategory] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const customCategories = state.customCategories || [];
  const categories = [
    ...customCategories.map((c) => ({ value: c.id, label: c.name })),
    { value: LEGACY_CATEGORY_OTHER, label: "Otros" }
  ];
  const defaultCategory = categories.find((c) => c.value !== "savings")?.value || LEGACY_CATEGORY_OTHER;
  const selectedCategory = category || defaultCategory;

  const handleAmountExtracted = ({ amount: a, currency: c }) => {
    setAmount(a);
    if (c) setCurrency(c);
    toast.success(`Detectado: ${(c === "USD" ? "US$" : "S/")} ${a.toFixed(2)}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!monthId) {
      toast.error("No se encontró el mes. Vuelve al detalle del mes e intenta de nuevo.");
      return;
    }
    if (!amount || amount <= 0) {
      toast.warning("Sube una foto del recibo para detectar el monto");
      return;
    }

    setIsLoading(true);
    try {
      await dispatch({
        type: "ADD_EXPENSE",
        payload: {
          monthId,
          name: name.trim() || "Gasto desde recibo",
          amount,
          currency: currency || "PEN",
          category: selectedCategory,
          date: getTodayDate(),
          note: null,
          status: "PAID",
          dueDay: null,
          isFromRecurring: false,
          recurringTemplateId: null,
          paidAt: new Date().toISOString()
        }
      });
      toast.success(`Gasto de ${formatDisplayAmount(amount, false, currency)} guardado`);
      navigate(`/month/${monthId}`);
    } catch (err) {
      console.error("Error al guardar:", err);
      toast.error("Error al guardar el gasto. Intenta de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (monthId) navigate(`/month/${monthId}`);
    else navigate("/");
  };

  return (
    <Layout>
      <div className="max-w-lg mx-auto">
        <PageHeader
          title="Agregar desde recibo"
          onBack={handleCancel}
          backAriaLabel="Volver"
        />

        <div className="bg-white rounded-2xl shadow-lg p-6">
          <p className="text-gray-600 text-sm mb-4">
            Toma una foto o sube la imagen del recibo. El monto se detectará automáticamente.
          </p>

          <ReceiptScanner
            onAmountExtracted={handleAmountExtracted}
            onError={(msg) => toast.warning(msg)}
            disabled={isLoading}
            autoScan
          />

          {amount !== null && (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4 pt-6 border-t-2 border-gray-100">
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl">
                <span className="text-green-700 font-semibold">
                  {CURRENCY_OPTIONS.find((o) => o.code === currency)?.symbol || "S/"} {amount.toFixed(2)}
                </span>
                <span className="text-green-600 text-sm">(detectado)</span>
              </div>

              <FormField label="Categoría" required>
                <select
                  value={selectedCategory}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500"
                  disabled={isLoading}
                >
                  {categories.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Nombre (opcional)">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Uber, Supermercado..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500"
                  disabled={isLoading}
                />
              </FormField>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Guardar gasto
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default QuickAddExpense;
